import React from 'react';
import { Link, useOutlet, useParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

import {
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  DocumentTextIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { type ColumnDef } from '@oysterjs/ui/data-table';

import {
  CaraWorkspaceArtifactType,
  ClientArtifactFieldsFragment,
  useGetClientArtifactsQuery,
  useRemoveClientArtifactMutation,
  useUpdateCaraWorkspaceArtifactsMutation
} from '~/api/graphql';
import { type RowAction, RowActionsMenu } from '~/components/row-actions-menu';
import {
  activeFiltersToClientArtifactFilter,
  clientDocumentSortInput,
  createClientArtifactFilterDefinitions,
  type ClientDocumentSortKey
} from '~/api/data/clients/client-document-filters';
import { LinkDocumentDialog } from '~/components/clients/client-document-picker';
import {
  CLIENT_TAB_QUERY_OPTIONS,
  ClientListTab,
  removeConfirmCopy,
  removeFailed,
  useClientConnection
} from '~/components/clients/client-list-tab';
import {
  ClientTabRoute,
  eachOf,
  selectionColumn,
  useClientListControls
} from '~/components/clients/client-panels';
import { clientArtifactsVariables } from '~/api/data/clients/variables';
import { Dash } from '~/components/clients/clients-columns';
import { dateFormatter } from '~/components/table-formatters';
import { type DocumentFilter } from '~/api/data/documents/documents-filters';
import { documentPreviewPath, getDisplayLabel } from '~/components/documents/documents.utils';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { ClientArtifactAmsCell } from '~/components/clients/client-artifact-ams-cell';
import { useClientArtifactSentEmails } from '~/components/clients/use-client-artifact-sent-emails';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';

type ClientArtifact = ClientArtifactFieldsFragment;

type ArtifactHandlers = {
  onArchive: (artifactId: string) => void;
  onRemove: (artifactId: string) => void;
};

/** The document viewer, slid out over this tab, on the latest version of any artifact type. */
export const artifactHref = (
  clientId: string,
  artifact: Pick<ClientArtifact, 'id' | 'latestVersion'> & {
    metadata: Pick<ClientArtifact['metadata'], 'workspaceId'>;
  }
) =>
  documentPreviewPath(
    `/app/clients/${clientId}/documents`,
    artifact.metadata.workspaceId,
    artifact.id,
    artifact.latestVersion
  );

/**
 * Archiving acts on the document itself and is undone from the archived
 * Documents view; removing acts on this client only.
 */
export const artifactRowActions = (
  clientId: string,
  artifact: Parameters<typeof artifactHref>[1],
  handlers: ArtifactHandlers
): RowAction[] => [
  {
    label: 'Open document',
    icon: ArrowTopRightOnSquareIcon,
    href: artifactHref(clientId, artifact)
  },
  { label: 'Archive', icon: ArchiveBoxIcon, onSelect: () => handlers.onArchive(artifact.id) },
  { label: 'Remove from client', icon: XMarkIcon, onSelect: () => handlers.onRemove(artifact.id) }
];

const titleOf = (artifact: ClientArtifact) => artifact.title || 'Untitled document';

const dataColumns = (
  clientId: string,
  handlers: ArtifactHandlers,
  hasAmsColumn: boolean,
  sentEmails: ReturnType<typeof useClientArtifactSentEmails>
): ColumnDef<ClientArtifact, ClientDocumentSortKey>[] => [
  {
    id: 'title',
    header: 'File name',
    sortKey: 'name',
    renderCell: (artifact) => (
      <span className="flex items-center gap-1.5 font-medium">
        <DocumentIcon className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500" />
        {titleOf(artifact)}
      </span>
    )
  },
  {
    id: 'type',
    header: 'Type',
    // A filled ACORD form is named for its form, which says more than "ACORD Form".
    renderCell: (artifact) =>
      artifact.type === CaraWorkspaceArtifactType.TemplateForm
        ? artifact.title
        : getDisplayLabel(artifact.type)
  },
  {
    id: 'workspace',
    header: 'Workspace',
    noHref: true,
    renderCell: (artifact) => (
      <Link
        to={`/app/workspaces/${artifact.workspace.id}`}
        className="text-primary-600 dark:text-primary-400 hover:underline"
      >
        {artifact.workspace.title || 'Untitled workspace'}
      </Link>
    )
  },
  {
    id: 'generatedBy',
    header: 'Generated by',
    renderCell: (artifact) => artifact.metadata.agencyUser?.name ?? <Dash />
  },
  {
    id: 'created',
    header: 'Date generated',
    sortKey: 'date',
    renderCell: (artifact) => dateFormatter.format(new Date(artifact.createdAt))
  },
  ...(hasAmsColumn
    ? [
        {
          id: 'amsLog',
          header: 'AMS',
          noHref: true,
          renderCell: (artifact: ClientArtifact) => (
            <ClientArtifactAmsCell
              artifact={artifact}
              state={sentEmails.states[artifact.id]}
              onRefresh={() => void sentEmails.refresh([artifact.id])}
            />
          )
        }
      ]
    : []),
  {
    id: 'actions',
    header: '',
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderCell: (artifact) => (
      <RowActionsMenu
        label={`Actions for ${titleOf(artifact)}`}
        actions={artifactRowActions(clientId, artifact, handlers)}
      />
    )
  }
];

const archiveFailed = (count: number) =>
  count === 1
    ? 'Could not archive one document. It is still listed.'
    : `Could not archive ${count} documents. They are still listed.`;

type PendingConfirm = { kind: 'archive' | 'remove'; ids: string[] };

const ClientDocumentsTable = ({ clientId }: { clientId: string }) => {
  const filterDefinitions = React.useMemo(() => createClientArtifactFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, sortBy, sortDirection, setSort } = useClientListControls<
    DocumentFilter,
    ClientDocumentSortKey
  >('date');

  const variables = React.useMemo(
    () =>
      clientArtifactsVariables(
        clientId,
        activeFiltersToClientArtifactFilter(activeFilters),
        clientDocumentSortInput(sortBy, sortDirection)
      ),
    [activeFilters, clientId, sortBy, sortDirection]
  );

  const query = useGetClientArtifactsQuery({ variables, ...CLIENT_TAB_QUERY_OPTIONS });
  const list = useClientConnection(query, query.data?.client?.artifacts, variables, {
    owner: { __typename: 'Client', id: clientId },
    fieldName: 'artifacts'
  });
  const { selected, setSelected, selectedSet, bulk } = list;
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  const sentEmails = useClientArtifactSentEmails(
    list.rows
      .filter((artifact) => artifact.type === CaraWorkspaceArtifactType.Email)
      .map((artifact) => artifact.id),
    isAmsLoggingEnabled
  );
  const hasAmsColumn =
    isAmsLoggingEnabled &&
    list.rows.some((artifact) => artifact.type === CaraWorkspaceArtifactType.Email);

  const [attaching, setAttaching] = React.useState(false);
  const [confirm, setConfirm] = React.useState<PendingConfirm | null>(null);
  const [removeArtifact] = useRemoveClientArtifactMutation();
  const [updateArtifacts] = useUpdateCaraWorkspaceArtifactsMutation();

  const runConfirmed = async () => {
    if (!confirm) return;
    const done =
      confirm.kind === 'archive'
        ? await bulk.run(
            'archive',
            async () => [
              await updateArtifacts({
                variables: { artifactIds: confirm.ids, input: { archived: true } }
              })
            ],
            () => archiveFailed(confirm.ids.length)
          )
        : await bulk.run(
            'remove',
            () =>
              eachOf(confirm.ids, (artifactId) =>
                removeArtifact({ variables: { clientId, artifactId } })
              ),
            removeFailed('document')
          );
    setConfirm(null);
    if (done) setSelected([]);
  };

  const columns = React.useMemo(
    () => [
      selectionColumn<ClientArtifact, ClientDocumentSortKey>(
        list.rows,
        selectedSet,
        setSelected,
        titleOf
      ),
      ...dataColumns(
        clientId,
        {
          onArchive: (id) => setConfirm({ kind: 'archive', ids: [id] }),
          onRemove: (id) => setConfirm({ kind: 'remove', ids: [id] })
        },
        hasAmsColumn,
        sentEmails
      )
    ],
    [clientId, list.rows, selectedSet, setSelected, hasAmsColumn, sentEmails]
  );

  return (
    <ClientListTab
      list={list}
      noun="documents"
      filterDefinitions={filterDefinitions}
      onFiltersChange={setActiveFilters}
      filtered={activeFilters.length > 0}
      placeholder="Filter by user, date, or search..."
      action={{ label: 'Attach document', onClick: () => setAttaching(true) }}
      empty={{
        icon: DocumentTextIcon,
        title: 'No generated documents',
        description: 'Documents Cara produces for this client are collected here.'
      }}
      columns={columns}
      rowHref={(artifact) => artifactHref(clientId, artifact)}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={setSort}
    >
      <FloatingSelectionBar
        count={selected.length}
        onClear={() => setSelected([])}
        onArchive={() => setConfirm({ kind: 'archive', ids: selected })}
        isArchiving={bulk.running === 'archive'}
        archiveIcon={<ArchiveBoxIcon className="size-3.5" />}
        extraActions={[
          {
            label: 'Remove from client',
            icon: <XMarkIcon className="size-3.5" />,
            onClick: () => setConfirm({ kind: 'remove', ids: selected }),
            loading: bulk.running === 'remove'
          }
        ]}
      />

      <ArchiveConfirmDialog
        open={confirm !== null}
        count={confirm?.ids.length ?? 0}
        itemNoun="document"
        {...(confirm?.kind === 'remove'
          ? removeConfirmCopy(
              'document',
              confirm.ids.length,
              'They stay in their workspaces and in Documents.'
            )
          : {})}
        loading={bulk.running !== null}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />

      <LinkDocumentDialog
        open={attaching}
        clientId={clientId}
        kind="artifact"
        onClose={() => setAttaching(false)}
        onAttached={list.refresh}
      />
    </ClientListTab>
  );
};

/** The Documents tab: what Cara generated for this client, across its workspaces. */
export const ClientDocumentsTab = () => {
  const { id = '' } = useParams();
  const outlet = useOutlet();
  // Outside the list's Await, so a revalidation that re-suspends the list
  // leaves an open document up.
  return (
    <>
      <ClientTabRoute noun="documents">
        {() => <ClientDocumentsTable clientId={id} />}
      </ClientTabRoute>
      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, {})}
      </AnimatePresence>
    </>
  );
};
